import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BookOpen, X, Maximize2, ChevronRight, ArrowRight,
    Search, FileText, BarChart2, CheckCircle, Layers, Activity
} from 'lucide-react';
import { chapterData } from '../../data/chapterData';

const iconMap = {
    BookOpen: <BookOpen className="w-6 h-6" />,
    Search: <Search className="w-6 h-6" />,
    FileText: <FileText className="w-6 h-6" />,
    BarChart2: <BarChart2 className="w-6 h-6" />,
    CheckCircle: <CheckCircle className="w-6 h-6" />,
    Layers: <Layers className="w-6 h-6" />,
    Activity: <Activity className="w-6 h-6" />
};

const ImageRenderer = ({ src, alt }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <div
                className="my-8 group cursor-zoom-in relative rounded-xl overflow-hidden border border-gray-700 shadow-2xl bg-gray-800/50"
                onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
            >
                <img
                    src={src}
                    alt={alt}
                    className="w-full h-auto max-h-[300px] object-contain mx-auto transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-black/50 p-2 rounded-full backdrop-blur-sm">
                        <Maximize2 className="w-6 h-6 text-white" />
                    </div>
                </div>
                <div className="p-3 bg-gray-900/90 text-center text-sm text-gray-400 border-t border-gray-700">
                    {alt}
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                            className="relative max-w-7xl max-h-[90vh] w-full flex flex-col items-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setIsOpen(false)}
                                className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                            <img
                                src={src}
                                alt={alt}
                                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl border border-gray-700"
                            />
                            <p className="mt-4 text-gray-300 text-lg font-medium text-center max-w-4xl">
                                {alt}
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export const ChapterDetails = () => {
    const [isReading, setIsReading] = useState(false);
    const [selectedChapter, setSelectedChapter] = useState(null);

    return (
        <section className="py-24 px-8 bg-gray-900 text-white relative z-10">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-16 flex justify-between items-end"
                >
                    <div>
                        <h2 className="text-4xl font-black tracking-tight mb-4">Thesis Structure</h2>
                        <p className="text-xl text-gray-400">
                            Explore the research journey chapter by chapter.
                        </p>
                    </div>
                    <button
                        onClick={() => setIsReading('/thesis.pdf')}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-blue-500/25 flex items-center gap-2"
                    >
                        <BookOpen className="w-5 h-5" />
                        Read Full Thesis
                    </button>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {chapterData.map((chapter, index) => (
                        <motion.div
                            key={chapter.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            onClick={() => setSelectedChapter(chapter)}
                            className="group cursor-pointer bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-blue-500/50 rounded-xl p-6 transition-all hover:shadow-xl hover:shadow-blue-900/10 hover:-translate-y-1"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 bg-gray-900 rounded-lg group-hover:bg-blue-500/10 group-hover:text-blue-400 transition-colors">
                                    {iconMap[chapter.icon] || <BookOpen className="w-6 h-6" />}
                                </div>
                                <span className="text-xs font-mono text-gray-500 group-hover:text-blue-400 transition-colors">
                                    CH {chapter.id.toString().padStart(2, '0')}
                                </span>
                            </div>

                            <h3 className="text-xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                                {chapter.title}
                            </h3>

                            <p className="text-gray-400 text-sm line-clamp-3 mb-4">
                                {chapter.objective}
                            </p>

                            <div className="flex items-center text-blue-400 text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-10px] group-hover:translate-x-0">
                                Explore Chapter <ArrowRight className="w-4 h-4 ml-2" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Chapter Detail Modal */}
            <AnimatePresence>
                {selectedChapter && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                        onClick={() => setSelectedChapter(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-gray-900 border border-gray-700 w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="p-8 border-b border-gray-800 flex justify-between items-start bg-gray-900 sticky top-0 z-10">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wider">
                                            Chapter {selectedChapter.id}
                                        </span>
                                    </div>
                                    <h2 className="text-3xl font-black text-white">{selectedChapter.title}</h2>
                                </div>
                                <button
                                    onClick={() => setSelectedChapter(null)}
                                    className="p-2 hover:bg-gray-800 rounded-full transition-colors text-gray-400 hover:text-white"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="overflow-y-auto p-8 custom-scrollbar">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                                    {/* Left Column: Text Content */}
                                    <div className="lg:col-span-2 space-y-8">
                                        <div>
                                            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                                Objective
                                            </h3>
                                            <p className="text-gray-300 text-lg leading-relaxed border-l-4 border-green-500/50 pl-4 bg-green-500/5 p-4 rounded-r-lg">
                                                {selectedChapter.objective}
                                            </p>
                                        </div>

                                        <div>
                                            <h3 className="text-lg font-bold text-white mb-3">Summary</h3>
                                            {selectedChapter.sections ? (
                                                <div className="space-y-6">
                                                    {selectedChapter.sections.map((section, idx) => (
                                                        <div key={idx}>
                                                            <h4 className="font-bold text-blue-400 mb-2">{section.title}</h4>
                                                            {section.content && (
                                                                <p className="text-gray-300 mb-2 leading-relaxed">
                                                                    {section.content}
                                                                </p>
                                                            )}
                                                            {section.items && (
                                                                <ul className="list-disc list-inside text-gray-400 space-y-1 ml-2">
                                                                    {section.items.map((item, i) => (
                                                                        <li key={i} className="leading-relaxed">{item}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-400 leading-relaxed">
                                                    {selectedChapter.summary}
                                                </p>
                                            )}
                                        </div>

                                        {/* Key Figures Section */}
                                        {selectedChapter.keyFigures && selectedChapter.keyFigures.length > 0 && (
                                            <div>
                                                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                                    <BarChart2 className="w-5 h-5 text-blue-500" />
                                                    Key Figures & Tables
                                                </h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {selectedChapter.keyFigures.map((fig, idx) => (
                                                        <div key={idx} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 hover:border-blue-500/30 transition-colors">
                                                            <ImageRenderer src={fig.src} alt={fig.alt} />
                                                            <p className="mt-3 text-sm text-gray-400 font-medium">
                                                                {fig.caption}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right Column: Actions & Context */}
                                    <div className="space-y-6">
                                        <div className="bg-blue-600/10 border border-blue-500/20 rounded-xl p-6">
                                            <h4 className="text-blue-400 font-bold mb-2">Dive Deeper</h4>
                                            <p className="text-sm text-gray-400 mb-4">
                                                Read the full text of this chapter to understand the complete methodology and findings.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    const pdfUrl = selectedChapter.startPage
                                                        ? `/thesis.pdf#page=${selectedChapter.startPage}`
                                                        : '/thesis.pdf';
                                                    setSelectedChapter(null);
                                                    setIsReading(pdfUrl);
                                                }}
                                                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                                            >
                                                <BookOpen className="w-4 h-4" />
                                                Read Full Chapter
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Full Screen Reader Modal (PDF Viewer) */}
            <AnimatePresence>
                {isReading && (
                    <motion.div
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: '100%' }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed inset-0 z-[70] bg-gray-950 flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-8 py-4 border-b border-gray-800 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/80">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-500/10 rounded-lg">
                                    <BookOpen className="w-6 h-6 text-blue-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">Thesis Reader</h3>
                                    <p className="text-sm text-gray-400">Normative Reinforcement Control Framework</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsReading(false)}
                                className="p-2 hover:bg-gray-800 rounded-full transition-colors text-gray-400 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* PDF Content */}
                        <div className="flex-1 bg-gray-900 overflow-hidden relative">
                            <iframe
                                src={isReading}
                                className="w-full h-full border-none"
                                title="Thesis PDF"
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
